import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  LinearProgress,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import ErrorIcon from '@mui/icons-material/Error';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import './css/VoiceSign.css';
import useCrypto from '../functions/useCrypto';
import useAxiosKeycloak from '../hooks/useAxiosKeycloak';
import getSignerActions from '../functions/signerActions';

const TOTAL_PHRASES = 2;
const FIRST_PHRASE_INSTRUCTION = 'Soy [di tu nombre completo], mi dni es [di tu DNI]';
const VOICE_PUBLIC_KEY =
  '-----BEGIN PUBLIC KEY-----' +
  'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAinGpyKvygcdktnSMGVay' +
  'sPyDeWt/GnTHOc7nXZbElZFCUBOAU3NPk6EHw6/BQKbRL4kBy8622KVGXXIQZDMr' +
  '4m1MZ2nX7PSVHbivlb8aI0e4O9S5KqLbNpfpOkt7rXaiPGylsJvMLwf4yLm3t1oz' +
  'KD5g+EoS52Vm4tcimASDyDEpU+2gLvBBbihGCpxiQ+m2LfTMyleFiLNuFGqVqkij' +
  'M/N1dlWKwz8DjwR0sE+n+jcE7zEyCw+qxdaOBtW6CqCCoFFsSWovV1AGwLxhjCgF' +
  'OsU+dPfFrx2xKXcvjJbdcwVADkrypCNuFQNLH3rS0f36U2Z/LsfMSZmYhqFdeZ3c' +
  'OQIDAQAB' +
  '-----END PUBLIC KEY-----';
const VOICE_SIGN_BASE64 =
  'PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMi43NSIgaGVp' +
  'CmlnaHQPSIyNC44MzMiIHZpZXdCb3g9IjAgMCAyMi43NSAyNC44MzMiPg0KICA8ZGVmcz4NCiAg' +
  'ICA8c3R5bGU+DQogICAgICAuY2xzLTEgew0KICAgICAgICBmaWxsOiAjMzE2MDk0Ow0KICAgICAg' +
  'fQ0KICAgIDwvc3R5bGU+DQogIDwvZGVmcz4NCiAgPHBhdGggYWQ9IlBpY3RvZ3JhbWFzXy1fQ2Fs' +
  'ZW5kYXJpb18iIGRhdGEtbmFtZT0iUGljdG9ncmFtYXMgLSBDYWxlbmRhcmlvICIgY2xhc3M9ImNs' +
  'cy0xIiBkPSJNMjI0Ljk4MSwzMjcuMzQySDI0NC4ydjE1LjRIMjI0Ljk4MVptMTUuOTMzLTcuNjY4' +
  'djMuMjkxSDIyOC4yN3YtMy4yOTFoLTMuMTU2djMuMjkxaC0xLjl2MjEuNTQyaDIyLjc1VjMyMi45' +
  'NjVoLTEuOXYtMy4yOTFaIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMjIzLjIxNCAtMzE5LjY3NCki' +
  'Lz4NCjwvc3ZnPg0K';
const VOICE_TIPS = [
  {
    id: 'duration',
    text: 'Asegurate de que la voz se grabe por más de 5 segundos y completá la frase sin cortar.',
  },
  {
    id: 'clarity',
    text: 'Hablá despacio, vocalizando cada palabra tal como aparece en pantalla.',
  },
  {
    id: 'silence',
    text: 'Elegí un ambiente silencioso, sin ruidos ni interrupciones cercanas.',
  },
];

const hasFailedMetricThresholds = (scores, thresholds) => {
  if (!scores || !thresholds) return false;

  return Object.entries(thresholds).some(([metricName, thresholdValue]) => {
    const scoreValue = scores[metricName];

    if (typeof thresholdValue !== 'number') return false;
    if (typeof scoreValue === 'number') return scoreValue < thresholdValue;

    const parsedScore = Number(scoreValue?.parsedValue ?? scoreValue?.source);
    return Number.isFinite(parsedScore) && parsedScore < thresholdValue;
  });
};

const VoiceSignComponent = ({ idDocument, keycloak, handleClose }) => {
  const axiosKeycloak = useAxiosKeycloak(keycloak);
  const [currentChallenge, setCurrentChallenge] = useState('');
  const [phrasesCompleted, setPhrasesCompleted] = useState(0);
  const [challengeRequestKey, setChallengeRequestKey] = useState(0);
  const [isFetchingChallenge, setIsFetchingChallenge] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState("Presioná 'Iniciar Grabación' y autorizá el uso del micrófono cuando el sistema lo solicite.");
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { encryptedData, encryptData } = useCrypto();

  // const baseURL = process.env.REACT_APP_BACKEND_URL; // No longer needed here
  // const agregarSign = process.env.REACT_APP_PUT_SIGN; // No longer needed here
  // const documentoUrl = baseURL + agregarSign; // No longer needed here

  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);

  const progressValue = (phrasesCompleted / TOTAL_PHRASES) * 100;
  const hasCompletedAllPhrases = phrasesCompleted >= TOTAL_PHRASES;
  const isInstructionPhase = phrasesCompleted === 0;
  const isVerificationPhase = !hasCompletedAllPhrases && !isInstructionPhase;
  const currentStepNumber = hasCompletedAllPhrases ? TOTAL_PHRASES : phrasesCompleted + 1;
  const isUiLocked = isRecording || isProcessing;
  const displayedPhrase = isInstructionPhase
    ? FIRST_PHRASE_INSTRUCTION
    : (currentChallenge || (isFetchingChallenge ? 'Obteniendo frase de verificación...' : 'Esperando nueva frase'));
  const formatConfidence = (confidence) => {
    if (confidence === undefined || confidence === null) return 'N/A';
    const numeric = Number(confidence);
    if (Number.isNaN(numeric)) return 'N/A';
    if (numeric <= 1) {
      return `${(numeric * 100).toFixed(2)}%`;
    }
    return `${numeric.toFixed(2)}%`;
  };

  useEffect(() => {
    if (!isVerificationPhase) return;

    let isMounted = true;

    const fetchChallengePhrase = async () => {
      setIsFetchingChallenge(true);
      setError('');
      setStatusMessage('Obteniendo la frase de verificación...');

      try {
        const challengeUrl = `${process.env.REACT_APP_VOICE_URL}liveness/challenge`;
        const response = await axiosKeycloak.get(challengeUrl);
        if (!isMounted) return;

        const phrase = response.data?.challenge_phrase;
        if (!phrase) {
          throw new Error('No se recibió la frase de desafío');
        }

        setCurrentChallenge(phrase);
        setStatusMessage("Presioná 'Iniciar Grabación' y autorizá el uso del micrófono cuando el sistema lo solicite.");
      } catch (err) {
        if (!isMounted) return;
        console.error('Error al obtener la frase de desafío:', err.response?.data || err);
        setError('No pudimos obtener la frase de verificación. Intentá nuevamente.');
        setStatusMessage('No pudimos obtener la frase de verificación. Reintentá nuevamente.');
      } finally {
        if (isMounted) {
          setIsFetchingChallenge(false);
        }
      }
    };

    fetchChallengePhrase();

    return () => {
      isMounted = false;
    };
  }, [axiosKeycloak, challengeRequestKey, isVerificationPhase]);

  const handleRetryChallenge = () => {
    if (!isVerificationPhase) return;
    setCurrentChallenge('');
    setError('');
    setStatusMessage('Obteniendo la frase de verificación...');
    setChallengeRequestKey(prev => prev + 1);
  };

  const startRecording = async () => {
    try {
      setError('');
      setSnackbarOpen(false);
      if (isVerificationPhase && !currentChallenge) {
        setError('Aún no tenemos una frase para grabar. Intentá obtener una nueva frase.');
        return;
      }

      setStatusMessage(`Grabando... Por favor, lee claramente: "${displayedPhrase}"`);
      setIsRecording(true);

      if (typeof window.MediaRecorder === 'undefined') {
        throw new Error('MediaRecorder no está disponible en este navegador.');
      }

      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        throw new Error('Este navegador no soporta grabaciones en audio/webm.');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      recordedChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        setError('Error durante la grabación de audio. Verificá el micrófono e intentá nuevamente.');
        setIsRecording(false);
        setIsProcessing(false);
      };

      mediaRecorder.start();
    } catch (err) {
      console.error(err);
      setError('Error al iniciar la grabación. Verifica el micrófono y los permisos.');
      setIsRecording(false);
    }
  };

  const stopRecording = async () => {
    setIsRecording(false);
    setIsProcessing(true);
    setStatusMessage('Se está procesando el audio...');

    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder) {
      setError('No hay una grabación activa para procesar. Intentá nuevamente.');
      setIsProcessing(false);
      return;
    }

    try {
      await new Promise(resolve => {
        mediaRecorder.addEventListener('stop', resolve, { once: true });
        mediaRecorder.stop();
      });
    } catch (err) {
      console.error('Error al detener la grabación:', err);
      setError('No pudimos detener la grabación correctamente. Intentá nuevamente.');
      setIsProcessing(false);
      return;
    }

    mediaStreamRef.current?.getTracks().forEach(track => track.stop());
    mediaStreamRef.current = null;
    mediaRecorderRef.current = null;

    const webmBlob = new Blob(recordedChunksRef.current, { type: 'audio/webm' });
    const verificationResult = await sendAudioToServer(webmBlob, phrasesCompleted);
    setIsProcessing(false);

    if (!verificationResult.success && !verificationResult.hasSpecificMessage) {
      setStatusMessage('Necesitamos volver a grabar la misma frase para validar tu identidad.');
    }
  };

  const sendAudioToServer = async (audioBlob, phraseIndex) => {
    const isInstructionStage = phraseIndex === 0;
    if (!isInstructionStage && !currentChallenge) {
      setError('No se encontró la frase a verificar. Intentá nuevamente.');
      setStatusMessage('Error. Solicitá una nueva frase e intentá otra vez.');
      return { success: false, hasSpecificMessage: true };
    }

    const userId =
      keycloak?.tokenParsed?.cuil ||
      keycloak?.tokenParsed?.preferred_username ||
      keycloak?.tokenParsed?.name ||
      'voice-user';

    const verifyUrl = `${process.env.REACT_APP_VOICE_URL}liveness/verify_phrase`;
    const enrollUrl = `${process.env.REACT_APP_VOICE_URL}enroll`;
    const isLastPhrase = phraseIndex >= TOTAL_PHRASES - 1;

    const enrollFormData = new FormData();
    enrollFormData.append('audio', audioBlob, 'audio.webm');
    enrollFormData.append('user_id', userId);

    try {
      setStatusMessage('Enviando audio para enrolamiento...');
      await axiosKeycloak.post(enrollUrl, enrollFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
      });

      if (!isLastPhrase) {
        setError('');
        setSnackbarOpen(true);
        setPhrasesCompleted(prev => prev + 1);
        setStatusMessage(`Paso ${phraseIndex + 1} de ${TOTAL_PHRASES} completado. Preparando la siguiente frase.`);
        setCurrentChallenge('');
        return { success: true, hasSpecificMessage: false };
      }

      setStatusMessage('Enrolamiento completado. Validando frase contra el servicio.');

      const formData = new FormData();
      formData.append('audio', audioBlob, 'audio.webm');
      formData.append('challenge_phrase', currentChallenge);
      formData.append('include_embedding_analysis', 'false');

      const response = await axiosKeycloak.post(verifyUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
      });

      console.log('Resultado de verificación de voz:', response.data);
      const isLive = response.data?.is_live;
      const confidenceScore = response.data?.confidence;
      const reason = response.data?.reason;
      const metricsFailedByReason =
        typeof reason === 'string' && reason.toLowerCase().includes('falla en métricas');
      const metricsFailedByThresholds = hasFailedMetricThresholds(
        response.data?.scores,
        response.data?.thresholds
      );
      const verificationPassed = Boolean(isLive) && !metricsFailedByReason && !metricsFailedByThresholds;

      if (!verificationPassed) {
        const friendlyReason = reason || 'La frase no fue reconocida correctamente.';
        const failureConfidence = formatConfidence(confidenceScore);
        const detailedReason = failureConfidence === 'N/A'
          ? friendlyReason
          : `${friendlyReason} Coincidencia: ${failureConfidence}.`;
        setError(detailedReason);
        setStatusMessage('No pudimos validar la frase. Intentá de nuevo leyendo exactamente lo que aparece en pantalla.');
        setSnackbarOpen(false);
        return { success: false, hasSpecificMessage: true };
      }

      setError('');
      setSnackbarOpen(true);
      const formattedConfidence = formatConfidence(confidenceScore);
      setPhrasesCompleted(prev => prev + 1);

      setStatusMessage(`Paso ${TOTAL_PHRASES} de ${TOTAL_PHRASES} completado. Coincidencia final: ${formattedConfidence}. Procesando tu firma de voz...`);
      setCurrentChallenge('');

      try {
        const downloadUrl = `${process.env.REACT_APP_VOICE_URL}embeddings/${userId}/download`;
        setStatusMessage('Descargando y encriptando datos biométricos...');
        const downloadResponse = await axiosKeycloak.get(downloadUrl);
        const embeddingsJson = downloadResponse.data?.embeddings;
        if (!embeddingsJson) {
          throw new Error('No se recibieron embeddings');
        }
        const jsonString = JSON.stringify(embeddingsJson);
        await encryptData(jsonString, VOICE_PUBLIC_KEY);
        setStatusMessage(`Paso ${TOTAL_PHRASES} de ${TOTAL_PHRASES} completado. Procesando tu firma de voz...`);
      } catch (downloadError) {
        console.error('Error al descargar o cifrar embeddings:', downloadError.response?.data || downloadError);
        setError('Error al procesar los datos biométricos. Inténtalo nuevamente.');
        setStatusMessage('Error al procesar los datos biométricos.');
          return { success: false, hasSpecificMessage: true };
      }
    } catch (err) {
      console.error('Error durante el enrolamiento o verificación de voz:', err.response?.data || err);
      setError('Error al enrolar o verificar el audio. Inténtalo nuevamente.');
      setStatusMessage('Error durante el proceso de firma por voz. Reintentá.');
      return { success: false, hasSpecificMessage: true };
    }

    return { success: true, hasSpecificMessage: false };
  };

  const [redirectMessage, setRedirectMessage] = useState('');
  useEffect(() => {
    const sendSignedData = async () => {
      if (encryptedData && hasCompletedAllPhrases) {
        try {
          const signingBackendUrl = `${process.env.REACT_APP_BACKEND_URL}${process.env.REACT_APP_PUT_SIGN}`;
          const dataToSend = {
            docId: idDocument,
            datosBiometricos: encryptedData,
            fecha: new Date().toISOString(),
            signBase64: VOICE_SIGN_BASE64,
            tipoFirma: "VOZ",
          };

          const signResponse = await axiosKeycloak.put(signingBackendUrl, dataToSend, {
            timeout: 20000,
          });

          if (signResponse.status >= 200 && signResponse.status < 300) {
            setStatusMessage("Firma de voz completada y enviada al backend de firma.");
          } else {
            throw new Error(`Respuesta inesperada del servidor: ${signResponse.status}`);
          }

          setStatusMessage("Firma de voz completada y enviada al backend de firma.");
          const signerActions = await getSignerActions(axiosKeycloak, idDocument);
          const response = await axiosKeycloak.get(`${process.env.REACT_APP_BACKEND_URL}${process.env.REACT_APP_DOCUMENT_STATE}?id=${idDocument}`);

          const estadoDelDocumento = response.data;
          if (estadoDelDocumento === 'CERRADO') {
            setRedirectMessage('Firma completada. Se le está redirigiendo a Documentos Firmados.');
            window.location.href = '/signed-documents/?id=' + idDocument;
          } else if (estadoDelDocumento === 'PUBLICADO' && !signerActions.puedeFirmar) {
            setRedirectMessage('Firma completada. Se le está redirigiendo a Documentos en Trámite.');
            window.location.href = '/documents-in-process/?id=' + idDocument;
          }
        } catch (signingError) {
          console.error('Error al enviar la firma al backend:', signingError.response?.data || signingError);
          setError('Error al enviar la firma al backend. Inténtalo nuevamente.');
          setStatusMessage('Error al enviar la firma.');
        }
      }
    };

    sendSignedData();
  }, [encryptedData, idDocument, keycloak, hasCompletedAllPhrases, axiosKeycloak]);


  return (
    <Container maxWidth="xs" sx={{ mt: 4 }}>
      <Box textAlign="center" mb={3} aria-hidden={isUiLocked}>
        <Typography
          component="p"
          className="sr-only"
          lang="es"
          aria-live="polite"
          aria-hidden={isUiLocked}
        >
          La pantalla presenta un boton para grabar la voz.
          Si no puede hacerlo, cierre esta pantalla y firme por trazo.
        </Typography>
        <RecordVoiceOverIcon color="primary" sx={{ fontSize: 60 }} />
        <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
          Firma de Voz Electrónica
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Grabaremos {TOTAL_PHRASES} frases generadas automáticamente para validar tu identidad.
        </Typography>
        <Box
          component="section"
          aria-label="Recomendaciones para grabar la firma de voz"
          sx={{ mt: 2 }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <LightbulbOutlinedIcon sx={{ color: '#316094' }} />
            <Typography variant="subtitle2" color="text.primary" fontWeight="medium">
              Tips antes de grabar
            </Typography>
          </Box>
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {VOICE_TIPS.map((tip, index) => (
              <Typography
                key={tip.id}
                component="li"
                variant="body2"
                color="text.secondary"
                sx={{ display: 'flex', gap: 1.5, mb: 0.5 }}
              >
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: '#316094',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {index + 1}.
                </Box>
                {tip.text}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>

      <Box sx={{ width: '100%', mb: 3 }} aria-hidden={isUiLocked}>
        <Typography variant="body2" color="text.secondary" align="right" sx={{ mb: 0.5 }}>
          Paso {currentStepNumber} de {TOTAL_PHRASES}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{ height: 10, borderRadius: 5 }}
          color={hasCompletedAllPhrases ? 'success' : 'primary'}
        />
      </Box>

      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          bgcolor: 'action.hover',
          borderLeft: `5px solid ${isRecording ? '#f44336' : '#2196f3'}`
        }}
        aria-hidden={isUiLocked}
      >
        <Typography variant="h6" gutterBottom fontWeight="medium">
          Frase a Grabar:
        </Typography>
        <Typography
          variant="h5"
          component="p"
          sx={{ fontWeight: 'bold', color: isRecording ? 'error.dark' : 'text.primary', fontStyle: 'italic' }}
          aria-live={isUiLocked ? "off" : "assertive"}
          aria-atomic="true"
        >
          "{displayedPhrase}"
        </Typography>
        {isVerificationPhase && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleRetryChallenge}
            disabled={isRecording || isProcessing || isFetchingChallenge}
            sx={{ mt: 2 }}
          >
            {currentChallenge ? 'Cambiar frase' : 'Obtener nueva frase'}
          </Button>
        )}
      </Box>

      <Box className="voice-sign-buttons" display="flex" justifyContent="center" mb={3}>
        <Button
          variant="contained"
          color={isRecording ? 'error' : 'primary'}
          onClick={isRecording ? stopRecording : startRecording}
          disabled={
            isProcessing ||
            isFetchingChallenge ||
            (isVerificationPhase && !currentChallenge) ||
            hasCompletedAllPhrases
          }
          startIcon={isRecording ? <StopIcon /> : <MicIcon />}
          size="large"
          sx={{ minWidth: 250, height: 60, fontSize: '1.2rem' }}
          aria-label={isRecording ? 'Detener grabación de voz' : 'Iniciar grabación de voz'}
        >
          {isRecording ? 'Detener Grabación' : 'Iniciar Grabación'}
        </Button>
      </Box>

      <Box textAlign="center" mt={2} height={40} aria-live={isRecording ? "off" : "polite"} aria-atomic="true">
        {isProcessing ? (
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            <CircularProgress size={24} />
            <Typography variant="body1" color="primary">
              {statusMessage}
            </Typography>
          </Box>
        ) : isRecording ? (
          <Typography variant="body1" color="error" fontWeight="bold">
            🔴 {statusMessage}
          </Typography>
        ) : (
          <Typography variant="body1" color="text.secondary">
            {statusMessage}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert
          severity="error"
          icon={<ErrorIcon />}
          sx={{ mt: 3 }}
          role="alert"
          aria-live={isRecording ? "off" : "assertive"}
        >
          {error}
        </Alert>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
       anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert role="alert" aria-live="assertive" onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {hasCompletedAllPhrases
            ? `Paso ${TOTAL_PHRASES} de ${TOTAL_PHRASES} completado. Procesando tu firma de voz...`
            : `Paso ${phrasesCompleted} de ${TOTAL_PHRASES} completado con éxito. Vamos a grabar la siguiente.`}
        </Alert>
      </Snackbar>
      <div
        aria-live="assertive"
        aria-atomic="true"
        aria-hidden={isRecording}
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        {snackbarOpen &&
          (hasCompletedAllPhrases
            ? `Paso ${TOTAL_PHRASES} de ${TOTAL_PHRASES} completado. Procesando tu firma de voz.`
            : `Paso ${phrasesCompleted} de ${TOTAL_PHRASES} completado con éxito. Vamos a grabar la siguiente frase.`)}
      </div>

      <div
        aria-live="assertive"
        aria-atomic="true"
        lang="es"
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        {redirectMessage}
      </div>
      <div
        aria-live="assertive"
        aria-atomic="true"
        lang="es"
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        {error ? `${error} ${statusMessage}` : ''}
      </div>
    </Container>
  );
};

export default VoiceSignComponent;
